import { useEffect, useMemo, useState } from "react";
import { divIcon, type Marker as LeafletMarker } from "leaflet";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { Crosshair, LocateFixed } from "lucide-react";
import { DEFAULT_MAP_CENTER } from "../../shops/constants";
import type { ShopLocation } from "../../shops/types";
import { hasExactLocation } from "../../shops/utils";

type MapSelectorProps = {
  value: ShopLocation;
  error?: string;
  onChange: (location: ShopLocation) => void;
};

function formatLatLng(latitude: number, longitude: number): string {
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

function toExactLocation(latitude: number, longitude: number): ShopLocation {
  return {
    latitude,
    longitude,
    addressText: formatLatLng(latitude, longitude),
    status: "exact"
  };
}

function MapClickHandler({ onSelect }: { onSelect: (latitude: number, longitude: number) => void }) {
  useMapEvents({
    click(event) {
      onSelect(event.latlng.lat, event.latlng.lng);
    }
  });

  return null;
}

function RecenterMap({ location }: { location: ShopLocation }) {
  const map = useMap();

  useEffect(() => {
    if (hasExactLocation(location)) {
      map.setView([location.latitude!, location.longitude!], Math.max(map.getZoom(), 13), {
        animate: true
      });
    }
  }, [location, map]);

  return null;
}

export function MapSelector({ value, error, onChange }: MapSelectorProps) {
  const [locationMessage, setLocationMessage] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const markerIcon = useMemo(
    () =>
      divIcon({
        className: "field-marker selected-marker",
        html: "<span></span>",
        iconSize: [28, 28],
        iconAnchor: [14, 14]
      }),
    []
  );

  const center = hasExactLocation(value)
    ? [value.latitude!, value.longitude!]
    : [DEFAULT_MAP_CENTER.latitude, DEFAULT_MAP_CENTER.longitude];

  const selectLocation = (latitude: number, longitude: number) => {
    onChange(toExactLocation(latitude, longitude));
    setLocationMessage(`Selected ${formatLatLng(latitude, longitude)}`);
  };

  const useCurrentLocation = () => {
    setLocationMessage(null);

    if (!navigator.geolocation) {
      setLocationMessage("This browser does not support location access.");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsLocating(false);
        selectLocation(position.coords.latitude, position.coords.longitude);
      },
      () => {
        setIsLocating(false);
        setLocationMessage("Location permission was denied or unavailable.");
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000
      }
    );
  };

  return (
    <section className="map-selector" aria-label="Shop location selector">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Location</p>
          <h2>Pin the shop</h2>
        </div>
        <button type="button" className="secondary-button compact-button" onClick={useCurrentLocation}>
          <LocateFixed size={16} />
          {isLocating ? "Locating..." : "Use my location"}
        </button>
      </div>

      <div className={`map-frame ${error ? "has-error" : ""}`}>
        <MapContainer center={center as [number, number]} zoom={12} scrollWheelZoom className="leaflet-map">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onSelect={selectLocation} />
          <RecenterMap location={value} />
          {hasExactLocation(value) ? (
            <Marker
              draggable
              icon={markerIcon}
              position={[value.latitude!, value.longitude!]}
              eventHandlers={{
                dragend(event) {
                  const marker = event.target as LeafletMarker;
                  const latLng = marker.getLatLng();
                  selectLocation(latLng.lat, latLng.lng);
                }
              }}
            />
          ) : null}
        </MapContainer>
      </div>

      <div className="map-help">
        <Crosshair size={15} />
        <span>{locationMessage ?? "Click the map, drag the pin, or use phone location."}</span>
      </div>
      {error ? <p className="field-error">{error}</p> : null}
    </section>
  );
}
